import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Spinner, Alert, ListGroup,Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './RandevuTakvimi.css'; // Takvim için yazdığımız modern stilleri burada da kullanalım
import api from './api';

// İki tarihi (gün, ay, yıl olarak) karşılaştıran yardımcı fonksiyon
const isSameDay = (a, b) => 
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

function KlinikDashboard({ theme }) {
  // Mevcut State'ler
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Yeni Eklenen State'ler
  const [tumRandevular, setTumRandevular] = useState([]);
  const [seciliTarih, setSeciliTarih] = useState(new Date());

  // Veri Çekme Fonksiyonu
  const verileriGetir = useCallback(async () => {
    try {
      const response = await api.get('/klinik/randevular');
      // Gelen tarih string'lerini Date objesine çeviriyoruz
      const events = response.data.map(event => ({
        ...event,
        start: new Date(event.start),
      }));
      setTumRandevular(events);
    } catch (err) {
      setError("Randevu verileri yüklenirken bir hata oluştu.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    verileriGetir();
  }, [verileriGetir]);

  // Seçilen güne ait randevuları filtreleyen ve sıralayan mantık
  const seciliGunRandevulari = useMemo(() => {
    return tumRandevular
      .filter(randevu => isSameDay(randevu.start, seciliTarih))
      .sort((a, b) => a.start - b.start); // Saate göre sırala
  }, [tumRandevular, seciliTarih]);

  if (loading) return <Spinner animation="border" variant={theme === 'dark' ? 'light' : 'dark'} />;
  if (error) return <Alert variant="danger">{error}</Alert>;

  return (
    <div>
      <h2 className={theme === 'dark' ? 'text-light' : 'text-dark'}>Yönetim Paneli</h2>
      <p className={theme === 'dark' ? 'text-white-50' : 'text-muted'}>
        Kliniğinizle ilgili genel işlemleri buradan yönetebilirsiniz.
      </p>
      
      {/* Hızlı Erişim Kartları (Mevcut kodunuz) */}
      <Row className="mt-4">
        <Col md={4} className="mb-3">
          <Card as={Link} to="/takvim" className="glass-card text-white h-100 text-decoration-none">
            <Card.Body className="text-center">
              <Card.Title as="h3">📅</Card.Title>
              <Card.Text className="fw-bold">Genel Takvim</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card as={Link} to="/diyetisyenler" className="glass-card text-white h-100 text-decoration-none">
            <Card.Body className="text-center">
              <Card.Title as="h3">👩‍⚕️</Card.Title>
              <Card.Text className="fw-bold">Diyetisyen Yönetimi</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4} className="mb-3">
          <Card as={Link} to="/danisanlar" className="glass-card text-white h-100 text-decoration-none">
            <Card.Body className="text-center">
              <Card.Title as="h3">👥</Card.Title>
              <Card.Text className="fw-bold">Danışan Listesi</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <hr className="text-white-50 my-4" />

      {/* YENİ EKLENEN TAKVİM VE AJANDA BÖLÜMÜ */}
      <Row>
        {/* Sol Sütun: Dikey Takvim */}
        <Col md={5} lg={4} className="mb-3">
          <Calendar
            onChange={setSeciliTarih}
            value={seciliTarih}
            locale="tr-TR"
            // İçinde randevu olan günleri işaretle
            tileClassName={({ date, view }) =>
              view === 'month' && tumRandevular.some(r => isSameDay(r.start, date)) ? 'randevu-var' : null
            }
          />
        </Col>

        {/* Sağ Sütun: Günlük Ajanda */}
        <Col md={7} lg={8}>
          <Card className="glass-card h-100">
            <Card.Header as="h5">
              {seciliTarih.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })} Ajandası
            </Card.Header>
            <ListGroup variant="flush" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {seciliGunRandevulari.length > 0 ? (
                seciliGunRandevulari.map(randevu => (
                  <ListGroup.Item key={randevu.id} className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{randevu.start.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</strong> - {randevu.title}
                      <div className={theme === 'dark' ? 'text-white-50' : 'text-muted'} style={{ fontSize: '0.9em' }}>
                        Dyt. {randevu.extendedProps.diyetisyenAdi}
                      </div>
                    </div>
                    <Button as={Link} to={`/randevu/${randevu.id}`} size="sm" variant={`outline-${theme === 'dark' ? 'info' : 'primary'}`}>
                      Detay
                    </Button>
                  </ListGroup.Item>
                ))
              ) : (
                <ListGroup.Item>
                  Bu tarih için planlanmış bir randevu bulunmuyor.
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default KlinikDashboard;